import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Send, Loader2, ShieldCheck, ImagePlus, X, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import AnnotatedImage from "@/components/AnnotatedImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        return d.toLocaleString("en-GB", {
            day: sameDay ? undefined : "2-digit",
            month: sameDay ? undefined : "short",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    } catch (err) {
        console.warn("[chat] timestamp parse failed:", err?.message);
        return iso;
    }
}

// Cookie-only auth (Phase B): `?token=` is no longer needed because the
// browser auto-sends `tm_auth` on same-origin image fetches.
function imgUrl(id) {
    return `${API}/files/${id}`;
}

export default function CaseChat({ caseId, myEmail, myRole }) {
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [pendingImage, setPendingImage] = useState(null); // {id,name,size}
    const [uploadingImg, setUploadingImg] = useState(false);
    const [annotating, setAnnotating] = useState(null); // message obj
    const listRef = useRef(null);
    const fileInputRef = useRef(null);
    const lastCountRef = useRef(0);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/cases/${caseId}/messages`);
            setMessages(data || []);
        } catch { /* silent on poll */ } finally { setLoading(false); }
    }, [caseId]);

    useEffect(() => {
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => {
        if (messages.length !== lastCountRef.current) {
            lastCountRef.current = messages.length;
            setTimeout(() => {
                const el = listRef.current;
                if (el) el.scrollTop = el.scrollHeight;
            }, 50);
        }
    }, [messages]);

    const pickImage = () => fileInputRef.current?.click();

    const onImagePicked = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!/^image\//.test(file.type) && !/\.(jpe?g|png|webp|gif)$/i.test(file.name)) {
            toast.error("Please pick a JPG / PNG / WEBP / GIF image");
            return;
        }
        if (file.size > 25 * 1024 * 1024) {
            toast.error("Image too large (max 25 MB)");
            return;
        }
        setUploadingImg(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(`${API}/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPendingImage(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadingImg(false);
        }
    };

    const send = async (e) => {
        e?.preventDefault?.();
        if ((!draft.trim() && !pendingImage) || sending) return;
        setSending(true);
        try {
            await axios.post(
                `${API}/cases/${caseId}/messages`,
                { text: draft.trim() || null, image_id: pendingImage?.id || null },
            );
            setDraft("");
            setPendingImage(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally { setSending(false); }
    };

    return (
        <div data-testid="case-chat" className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col h-[560px]">
            <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-2.5 bg-gradient-to-r from-slate-50 to-white">
                <h3 className="font-display text-[16px] tracking-tight text-slate-900">Case discussion</h3>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-medium border border-emerald-100"><ShieldCheck size={11} /> Compliance log</span>
                <span className="ml-auto text-[11.5px] text-slate-500 font-mono-tr">{messages.length} {messages.length === 1 ? "message" : "messages"}</span>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/30" data-testid="case-chat-list">
                {loading ? (
                    <div className="flex justify-center items-center h-full text-slate-400"><Loader2 className="animate-spin" /></div>
                ) : messages.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center px-6">
                        <MessageSquare size={24} className="text-slate-300 mb-2" />
                        <div className="font-display text-slate-900 text-[16px]">No messages yet.</div>
                        <div className="mt-1 text-[12.5px] text-slate-500 max-w-xs">
                            {myRole && myRole !== "client" ? "Reply to the clinic — questions about design, shade verification, delivery. You can also attach an image and draw on it (blue annotations)." : "Message the lab — ask about shade, fit, or design tweaks. Attach a clinical photo and draw on it (green annotations) to point out details."}
                        </div>
                    </div>
                ) : (
                    messages.map((m) => {
                        const mine = m.author_email === myEmail;
                        const isLab = m.author_role === "Lab";
                        const hasImage = !!m.image_id;
                        const labStrokes = (m.annotations || []).filter((s) => (s.color || "").toLowerCase().startsWith("#1d4ed") || s.author_role === "Lab").length;
                        const clinicStrokes = (m.annotations || []).filter((s) => (s.color || "").toLowerCase().startsWith("#0596") || s.author_role === "Clinic").length;
                        return (
                            <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed shadow-sm ${
                                    mine ? "bg-[#0B1120] text-white rounded-br-md" :
                                    isLab ? "bg-cyan-50 text-slate-900 border border-cyan-100 rounded-bl-md" :
                                            "bg-white text-slate-900 border border-slate-200 rounded-bl-md"
                                }`}>
                                    <div className={`flex items-center gap-2 mb-1 font-mono-tr text-[10px] tracking-[0.16em] uppercase ${mine ? "text-cyan-300/80" : isLab ? "text-cyan-700" : "text-slate-500"}`}>
                                        <span className="font-semibold">{m.author_name}</span>
                                        <span>·</span><span>{m.author_role}</span>
                                        <span>·</span><span>{fmtTime(m.created_at)}</span>
                                    </div>
                                    {hasImage && (
                                        <button
                                            onClick={() => setAnnotating(m)}
                                            data-testid={`msg-image-${m.id}`}
                                            className="block w-full rounded-lg overflow-hidden border border-black/10 mt-1 mb-1.5 relative group"
                                            style={{ minHeight: 90 }}
                                        >
                                            <img src={imgUrl(m.image_id)} alt="" className="block w-full max-h-72 object-cover" loading="lazy" />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end p-2">
                                                <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-[11px] font-medium bg-black/70 px-2 py-1 rounded">Open · annotate →</span>
                                            </div>
                                            {(labStrokes + clinicStrokes) > 0 && (
                                                <div className="absolute top-1.5 right-1.5 flex items-center gap-1 bg-black/65 backdrop-blur text-white px-1.5 py-0.5 rounded text-[10px] font-mono-tr">
                                                    {labStrokes > 0 && <><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3b82f6]" /> {labStrokes}</>}
                                                    {clinicStrokes > 0 && <><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#10b981] ml-1" /> {clinicStrokes}</>}
                                                </div>
                                            )}
                                        </button>
                                    )}
                                    {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {pendingImage && (
                <div className="border-t border-slate-200 px-4 py-2.5 bg-slate-50 flex items-center gap-3" data-testid="pending-image-bar">
                    <img src={imgUrl(pendingImage.id)} alt="" className="h-10 w-10 rounded object-cover border border-slate-200" />
                    <div className="flex-1 min-w-0 text-[12.5px] text-slate-700 truncate">
                        <span className="font-medium">{pendingImage.name}</span>
                        <span className="ms-2 font-mono-tr text-[11px] text-slate-500">{Math.round(pendingImage.size / 1024)} KB</span>
                    </div>
                    <button onClick={() => setPendingImage(null)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200" title="Remove image" data-testid="pending-image-remove"><X size={14} /></button>
                </div>
            )}

            <form onSubmit={send} className="border-t border-slate-200 p-3 flex items-end gap-2 bg-white">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onImagePicked} data-testid="case-chat-file-input" />
                <button type="button" onClick={pickImage} disabled={uploadingImg} data-testid="case-chat-attach"
                    className="p-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50" title="Attach image (clinical photo / X-ray / shade reference)">
                    {uploadingImg ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                </button>
                <textarea
                    data-testid="case-chat-input" value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    rows={1}
                    placeholder={pendingImage ? "Add a caption (optional) · Enter to send" : (myRole && myRole !== "client" ? "Reply to clinic… (Enter to send · Shift-Enter for newline)" : "Message the lab… (Enter to send)")}
                    className="flex-1 resize-none px-3.5 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 text-[14px] max-h-32"
                />
                <button type="submit" disabled={sending || (!draft.trim() && !pendingImage)} data-testid="case-chat-send"
                    className="px-4 py-2.5 rounded-xl bg-[#0B1120] text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 text-[13px] font-medium">
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
                </button>
            </form>

            {annotating && (
                <AnnotatedImage
                    caseId={caseId}
                    message={annotating}
                    myRole={myRole}
                    onClose={() => setAnnotating(null)}
                    onSaved={() => { setAnnotating(null); load(); }}
                />
            )}
        </div>
    );
}
